import type { Sheet } from "@bandstand/core/chordPro";
import { observer } from "mobx-react-lite";
import type { CSSProperties, ReactNode, WheelEvent } from "react";
import styled, { css } from "styled-components";

import { useStores } from "../../stores/useStores";
import { color } from "../../theme/color";
import type { Arranging } from "./SheetLineView";
import { SheetLineView } from "./SheetLineView";

type SheetViewProps = {
    sheet: Sheet;
    lyricsOnly: boolean;
    textSize: number;
    columns?: boolean;
    /** Whether a section may be split over two columns to fill them. */
    splitSections?: boolean;
    arranging?: Arranging;
    /** Shown after the song, like next and previous. */
    children?: ReactNode;
};

/**
 * Lyrics with chords above them, or lyrics only. Sizes follow the text size, so everything grows together. The
 * spacing and the font come from the appearance settings.
 *
 * With `columns`, the song fills the height it's given from top to bottom, and only what doesn't fit goes on in a
 * next column to the right, scrolling sideways when there's no room for it. A section moves to the next column as
 * a whole when it fits there, and is only split, between lines, when it's taller than a column, or with
 * `splitSections`.
 * With `arranging`, the chords can be moved.
 */
export const SheetView = observer(function SheetView(props: SheetViewProps) {
    const { sheet, lyricsOnly, textSize, columns = false, splitSections = false, arranging, children } = props;
    const { display } = useStores();

    const look = {
        fontSize: textSize,
        "--line-spacing": display.lineSpacing,
        "--section-spacing": `${display.sectionSpacing}em`,
        "--chord-size": display.chordSize,
        "--sheet-font": SHEET_FONTS[display.font],
    } as CSSProperties;

    return (
        <Columns $columns={columns} style={look} onWheel={columns ? scrollSideways : undefined}>
            {sheet.sections.map((section, index) => (
                <Section key={index} $split={splitSections}>
                    {section.label && <Label>{section.label}</Label>}
                    {section.lines.map((line, i) => (
                        <SheetLineView key={i} line={line} lyricsOnly={lyricsOnly} arranging={arranging} />
                    ))}
                </Section>
            ))}
            {children && <After>{children}</After>}
        </Columns>
    );
});

/** The fonts the lyrics can be shown in. */
const SHEET_FONTS = {
    sans: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
    serif: 'Charter, "Iowan Old Style", Georgia, "Times New Roman", serif',
};

/** A mouse wheel scrolls up and down, but the columns go sideways. */
function scrollSideways(event: WheelEvent<HTMLElement>) {
    if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) event.currentTarget.scrollLeft += event.deltaY;
}

const Columns = styled.div<{ $columns: boolean }>`
    ${({ $columns }) =>
        $columns &&
        css`
            height: 100%;
            column-width: 26em;
            column-gap: 56px;
            column-fill: auto;
            overflow-x: auto;
            overflow-y: hidden;
        `}
`;

const Section = styled.section<{ $split: boolean }>`
    break-inside: ${({ $split }) => ($split ? "auto" : "avoid")};
    margin-bottom: var(--section-spacing, 1.2em);

    /* A line's chords stay with its lyrics, and a section's label with its first line. */
    & > * {
        break-inside: avoid;
    }
`;

const Label = styled.h2`
    margin: 0 0 0.3em;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${color.textMuted};
    break-after: avoid;
`;

const After = styled.div`
    break-inside: avoid;
`;
